/**
 * Os bônus — os do app e os de cada produto.
 *
 * Há dois tipos:
 *
 *   1. Os três bônus do app (Té Dorado, Lista Negra, Recetas Metabólicas):
 *      páginas internas trilíngues, de todo mundo. Não saem de query nenhuma
 *      e não têm capa: são texto.
 *
 *   2. Os bônus de UM produto, prometidos na oferta dele: os três do Clean
 *      Cakes, os três do Clean Meals, a Lista Enterrada e o "Como medir" do
 *      Clean Remedies e da Botica. Até aqui só apareciam dentro da página do
 *      produto, atrás de "Included with your purchase" — e "não acho o bônus
 *      que me prometeram" está entre as queixas das avaliações. Agora a home
 *      lista todos os bônus de quem comprou, cada um com o nome do produto.
 *
 * A lista dos três do app vivia dentro de TodosContenidos.tsx. Saiu de lá
 * para cá em vez de nascer uma segunda cópia que um dia divergiria.
 */
import { BookOpen, Coffee, Pill, Ruler, ShieldOff, type LucideIcon } from "lucide-react";

import { CLEAN_CAKES_BONUSES, bonusRoute as rotaBonusCleanCakes } from "@/pages/cleanCakes/bonuses";
import { CLEAN_MEALS_BONUSES, bonusRoute as rotaBonusCleanMeals } from "@/pages/cleanMeals/bonuses";
import { CLEAN_BAKES_BONUSES, bonusRoute as rotaBonusCleanBakes } from "@/pages/cleanBakes/bonuses";
import { BONUS_CEM } from "@/pages/cleanMornings/bonus";
import { CEM_NOME, CEM_PREFIX, CEM_PRODUCT_ID } from "@/lib/cleanMornings";

export type BonusModule = {
  id: string;
  titleKey: string;
  route: string;
  icone: LucideIcon;
};

export const BONUS_MODULES: BonusModule[] = [
  {
    id: "bonus-te-dorado",
    titleKey: "more.bonus_te_dorado_title",
    route: "/bonus/te-dorado",
    icone: Coffee,
  },
  {
    id: "bonus-lista-negra",
    titleKey: "more.bonus_lista_negra_title",
    route: "/bonus/lista-negra",
    icone: Pill,
  },
  {
    id: "bonus-recetas-metabolicas",
    titleKey: "more.bonus_recetas_title",
    route: "/bonus/recetas-metabolicas",
    icone: BookOpen,
  },
];

/** Um bônus pronto para a tela: nome já resolvido, produto de origem, rota. */
export type BonusNaTela = {
  id: string;
  nome: string;
  /** Nome do produto que deu o bônus; "Glucofy" para os bônus do app. */
  produto: string;
  route: string;
  icone: LucideIcon;
};

const CLEAN_CAKES = "8495830";
const CLEAN_MEALS = "7381661";
const CLEAN_BAKES = ["8061842", "7456362"]; // front do CEB e upsell nos outros funis
const CLEAN_REMEDIES = "8073539";
const LA_BOTICA = "8131891";

/**
 * Todos os bônus de quem tem estes produtos, na ordem: os do produto primeiro
 * (são os prometidos na oferta), os três do app por último.
 *
 * `temProduto` é o mesmo teste de posse do resto do app (owned ou
 * subscription). `t` resolve os nomes traduzidos; os bônus do Clean Cakes e
 * do Clean Meals têm nome só em inglês, como o produto.
 */
export function bonusesDeQuemTem(
  temProduto: (productId: string) => boolean,
  t: (key: string, fallback?: string) => string,
): BonusNaTela[] {
  const lista: BonusNaTela[] = [];

  if (temProduto(CLEAN_CAKES)) {
    for (const b of CLEAN_CAKES_BONUSES) {
      lista.push({ id: `clean-cakes-${b.slug}`, nome: b.title, produto: "Clean Cakes", route: rotaBonusCleanCakes(b.slug), icone: b.icone });
    }
  }

  if (temProduto(CLEAN_MEALS)) {
    for (const b of CLEAN_MEALS_BONUSES) {
      lista.push({ id: `clean-meals-${b.slug}`, nome: b.title, produto: "Clean Meals", route: rotaBonusCleanMeals(b.slug), icone: b.icone });
    }
  }

  // Clean Bakes: os três prometidos no funil do Clean Cakes (voz da Nora, "your
  // Sweetener Decoder"). Só para quem tem os dois produtos.
  if (CLEAN_BAKES.some(temProduto) && temProduto(CLEAN_CAKES)) {
    for (const b of CLEAN_BAKES_BONUSES) {
      lista.push({ id: `clean-bakes-${b.slug}`, nome: b.title, produto: "Clean Bakes", route: rotaBonusCleanBakes(b.slug), icone: b.icone });
    }
  }

  // Clean Mornings: os três da VSL v1.2, para quem tem o produto (8597307).
  if (temProduto(CEM_PRODUCT_ID)) {
    for (const b of BONUS_CEM) {
      lista.push({ id: `clean-mornings-${b.slug}`, nome: b.titulo, produto: CEM_NOME, route: `${CEM_PREFIX}/bonuses/${b.slug}`, icone: b.icone });
    }
  }

  if (temProduto(CLEAN_REMEDIES)) {
    lista.push(
      { id: "clean-remedies-buried-list", nome: t("clean_remedies.buried_list_title", "The Buried List"), produto: "Clean Remedies", route: "/clean-remedies/buried-list", icone: ShieldOff },
      { id: "clean-remedies-how-to-measure", nome: t("clean_remedies.how_to_measure_title", "How to Measure"), produto: "Clean Remedies", route: "/clean-remedies/how-to-measure", icone: Ruler },
    );
  }

  if (temProduto(LA_BOTICA)) {
    lista.push(
      { id: "la-botica-buried-list", nome: t("la_botica.buried_list_title", "La Lista Enterrada"), produto: "La Botica de la Abuela", route: "/la-botica/buried-list", icone: ShieldOff },
      { id: "la-botica-how-to-measure", nome: t("la_botica.how_to_measure_title", "Cómo Medir"), produto: "La Botica de la Abuela", route: "/la-botica/how-to-measure", icone: Ruler },
    );
  }

  for (const b of BONUS_MODULES) {
    lista.push({ id: b.id, nome: t(b.titleKey), produto: "Glucofy", route: b.route, icone: b.icone });
  }

  return lista;
}
